import { Fragment, memo, RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { User } from '../../../../../types';
import { goToExploreClubShowView, goToExploreGroupShowView, goToExploreMissMFCView, goToExploreNewView, goToExplorePopularView, goToExplorePrivateShowView, goToExploreTrendingView, goToHome } from '../../../../actions/nav';
import { ListType } from '../../../../constants/enums';
import { locales } from '../../../../locales/en';
import { openSettingsDrawer } from '../../../../state/modals/state.drawer';
import { AppRouter } from '../../../../state/state.router';
import { useList } from '../../../Hooks/useList';
import { useRouteSelector } from '../../../Hooks/useRoute';
import { useSearch } from '../../../Hooks/useSearch';
import Link from '../../Link';
import "./QuickAccess.linaria.module.css!=!./QuickAccess?./QuickAccess.linaria.module.css?css=H4sIAAAAAAAAA2XQ0U6EMBCF4XvfpWSLaGp9mIbCUKrTTqXjLrXx3Y0aDODln5xvLqbpOZinbKR1frZvdfQ5YV/0QJEhcn7+vGu+Jw6Mw3KbwrEq+swic0EQXBLoSBE282JNontrVQ0+ipsfedatbNO6DfDVwDqwsseqqR9HH51AmFjLxx3AA8AzWLybT4KMLF1HOJ3yzzAl3e0AHAH8A5aYKezNoIykxbaxP2WdKLLI/gP0pVEPC4SNXIsJ7rq+l/r7F6kuP/e+AOe05ZuQAQAA"
const listStyles = "atm_9s_1bgihbq atm_ge_glywfm";
const quickAccessStyles = "atm_jb_po3bb8";
const RANGE_STEP_SIZE = 300;
const sectionStyles = "atm_lk_exct8b atm_ll_exct8b atm_lo_1y44olf atm_le_1y44olf atm_c8_1orb2na";
const sectionContentStyles = "atm_vy_mgvxuy";
interface DynamicLinksProps {
  gridRef: RefObject<HTMLDivElement>;
  models: Array<User>;
  modelCount: number;
}
const DynamicLinks = memo(({
  models,
  modelCount,
  gridRef
}: DynamicLinksProps) => {
  const skipToLinksCount = Math.floor(models.length / RANGE_STEP_SIZE);
  const scrollToFirst = useCallback(() => {
    gridRef.current?.scrollTo(0, 0);
  }, [gridRef]);
  const scrollToLast = useCallback(() => {
    gridRef.current?.scrollTo(0, gridRef.current.scrollHeight);
  }, [gridRef]);
  const scrollToPosition = useCallback((index: number) => {
    const targetPosition = RANGE_STEP_SIZE * (index + 1);
    const scrollPercentage = targetPosition / models.length;
    const targetY = gridRef.current?.scrollHeight * scrollPercentage;
    gridRef.current?.scrollTo(0, targetY);
  }, [models.length, gridRef]);
  const skipToLinks = useMemo(() => Array.from({
    length: skipToLinksCount
  }).map((_, key) => <Fragment key={key}>
                        <span data-testid="Container__QuickAccess__SkipToLinks">
                            {' '}
                            <Link mfc actionLink onClick={() => scrollToPosition(key)}>
                                {RANGE_STEP_SIZE * (key + 1)}
                            </Link>
                            ..&nbsp;
                        </span>
                        {(key + 1) % 3 === 0 && <br />}{' '}
                    </Fragment>), [skipToLinksCount, scrollToPosition]);
  return <div className={sectionStyles}>
                <strong>Skip down to...</strong>
                <div className={sectionContentStyles}>
                    <Link mfc actionLink onClick={scrollToFirst}>
                        #1
                    </Link>
                    ..&nbsp;
                    {skipToLinks}
                    of&nbsp;
                    <Link mfc actionLink onClick={scrollToLast} data-testid="Link__QuickAccess__SkipToLast">
                        {modelCount}
                    </Link>
                    &nbsp;models.
                </div>
            </div>;
});
interface QuickAccessProps {
  modelCount: number;
  onClear?(): void;
}
const QuickAccess = ({
  modelCount,
  onClear
}: QuickAccessProps) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const route = useRouteSelector('visiblePage');
  const home = '';
  const isHome = route === home;
  const {
    query
  } = useSearch();
  const {
    fromHomePage,
    search
  } = query;
  const isHomeSearch = fromHomePage && search !== undefined;
  const models = useList(ListType.DEFAULT, {
    passive: false
  });
  const newModels = useList(ListType.NEWMODELS, {
    passive: false
  });
  const groupShows = useList(ListType.MODEL_GROUP_SHOWS, {
    passive: false
  });
  const clubShows = useList(ListType.MODEL_CLUB_SHOWS, {
    passive: false
  });
  const privateShows = useList(ListType.MODEL_PRIVATE_SHOWS, {
    passive: false
  });
  const popular = useList(ListType.POPULAR, {
    passive: false
  });
  const trending = useList(ListType.MODEL_TRENDING, {
    passive: false
  });
  const missMFC = useList(ListType.MISSMFC_ONLINE, {
    passive: false
  });
  useEffect(() => {
    if (isHome) gridRef.current = document.querySelector('.live-preview-grid');
  }, [isHome]);
  useEffect(() => {
    AppRouter.events.onCompleted(event => {
      if (event.pendingRoute === home) {
        // delay attaching ref for 100ms
        // to allow the list to render after route change
        setTimeout(() => {
          gridRef.current = document.querySelector('.live-preview-grid');
        }, 100);
      }
    });
  }, []);
  const returnHome = useCallback(() => {
    if (isHomeSearch) onClear?.();
    goToHome();
  }, [isHomeSearch, onClear]);
  return <div data-testid="Container__QuickAccess" className={quickAccessStyles}>
            {isHome && !isHomeSearch && <DynamicLinks gridRef={gridRef} models={models} modelCount={modelCount} />}
            <div className={sectionStyles}>
                <div>
                    <strong>View Online... </strong>
                </div>
                <ul data-testid="List__QuickAccess" className={listStyles}>
                    <li data-testid="List__QuickAccess__Item__AllModels">
                        <Link mfc actionLink onClick={returnHome}>
                            {isHomeSearch ? 'All ' : ''}Models
                        </Link>{' '}
                        ({models.length})
                    </li>
                    <li data-testid="List__QuickAccess__Item__NewModels">
                        <Link mfc actionLink onClick={goToExploreNewView}>
                            New Models
                        </Link>{' '}
                        ({newModels.length})
                    </li>
                    <li data-testid="List__QuickAccess__Item__GroupShows">
                        <Link mfc actionLink onClick={goToExploreGroupShowView}>
                            Group Shows
                        </Link>{' '}
                        ({groupShows.length})
                    </li>
                    <li data-testid="List__QuickAccess__Item__PrivateShows">
                        <Link mfc actionLink onClick={goToExplorePrivateShowView}>
                            Private Shows
                        </Link>{' '}
                        ({privateShows.length})
                    </li>
                    <li data-testid="List__QuickAccess__Item__ClubShows">
                        <Link mfc actionLink onClick={goToExploreClubShowView}>
                            Club Shows
                        </Link>{' '}
                        ({clubShows.length})
                    </li>
                    <li data-testid="List__QuickAccess__Item__Popular">
                        <Link mfc actionLink onClick={goToExplorePopularView}>
                            Popular
                        </Link>{' '}
                        ({popular.length})
                    </li>
                    <li data-testid="List__QuickAccess__Item__Trending">
                        <Link mfc actionLink onClick={goToExploreTrendingView}>
                            Trending
                        </Link>{' '}
                        ({trending.length})
                    </li>
                    <li data-testid="List__QuickAccess__Item__MissMFC">
                        <Link mfc actionLink onClick={goToExploreMissMFCView}>
                            Miss MFC
                        </Link>{' '}
                        ({missMFC.length})
                    </li>
                </ul>
            </div>
            {isHome && !isHomeSearch && <div data-testid="Container__QuickAccess__Settings" className={sectionStyles}>
                    Adjust your{' '}
                    <Link mfc actionLink onClick={() => openSettingsDrawer()}>
                        {locales.settings.drawerTitle}
                    </Link>{' '}
                    to filter and sort the model list.
                </div>}
        </div>;
};
export default memo(QuickAccess);